import {
  useGetCategoryQuery,
  useGetComboPackagesQuery,
  useGetMenuItemsQuery,
  useLazyGetBookingOrderDetailQuery,
  usePaymentProceedMutation,
} from "@/store/services/foodbooking";
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Center,
  Flex,
  FlexProps,
  Grid,
  GridItem,
  GridItemProps,
  Heading,
  HeadingProps,
  Spinner,
  SpinnerProps,
  Tab,
  TabList,
  TabListProps,
  TabProps,
  Tabs,
  TabsProps,
  Text,
  TextProps,
  useDisclosure,
} from "@chakra-ui/react";
import { BookingSummary, CartItems, SnackItems } from "@picovico/ui";
import { VoucherValidateData } from "types/api";

import { useWebFeature } from "@/hooks/useWebFeature";
import {
  catchErrorHandler,
  formatMsToMinutesSeconds,
  getLocalStorageData,
} from "@picovico/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import VoucherField, { VoucherFieldProps } from "./VoucherField";

import { useAppToast } from "@/hooks/useAppToast";
import { useOrderSession } from "@/hooks/useOrderSession";
import { getPageRoutes } from "@/routes/pageRoutes";
import { useClearSeatSelectionMutation } from "@/store/services/audi";
import { useLazyGetBookingDetailQuery } from "@/store/services/booking";
import { GridProps } from "@chakra-ui/system";
import { BookingSummaryProps } from "@picovico/ui/modules/FoodSummary/BookingSummary";
import { SnackItemsProps } from "@picovico/ui/modules/FoodSummary/SnackItems";
import { capitalize } from "lodash";
import ms from "ms";
import Error from "next/error";
import { useRouter } from "next/router";
import { BiChevronLeft } from "react-icons/bi";
import TimesUpModal, { TimesUpModalProps } from "../Modals/TimesUpModal";
import FoodBookingSkeleton, {
  FoodBookingSkeletonProps,
} from "./FoodBookingSkeleton";

interface FoodBookingProps {
  showGoBack?: boolean;
  showTimeRemaining?: boolean;
  containerWrapperProps?: BoxProps;
  topBoxProps?: FlexProps;
  goBackButtonProps?: ButtonProps;
  timeRemainingTextProps?: TextProps;
  listingGridProps?: GridProps;
  listingGridItemProps?: GridItemProps;
  headerBoxProps?: BoxProps;
  headerTextProps?: TextProps;
  headerSubText?: string;
  headerSubTextProps?: TextProps;
  snackBoxProps?: BoxProps;
  snackHeadingProps?: HeadingProps;
  snackDescriptionProps?: TextProps;
  tabsProps?: TabsProps;
  tabListProps?: TabListProps;
  tabItemProps?: TabProps;
  bookingGridProps?: GridProps;
  snackItemProps?: SnackItemsProps;
  spinnerProps?: SpinnerProps;
  noMoreItemsTextProps?: TextProps;
  bookingSummaryProps?: BookingSummaryProps;
  timesUpModalProps?: TimesUpModalProps;
  skeletonProps?: FoodBookingSkeletonProps;
  voucherFieldProps?: Partial<VoucherFieldProps>;
}

const sortByDisplayOrder = (a: any, b: any) =>
  (a.display_order ?? Infinity) - (b.display_order ?? Infinity);

const COMBO_TAB = "combo";

const mapToSnackItem = (item: any, cartItems: CartItems[]) => ({
  idx: item.idx,
  name: item.name,
  price: item.price,
  image: item.image,
  description: item.description,
  combo_package_items: item?.combo_package_items,
  quantity: cartItems.find((x) => x.idx === item.idx)?.quantity || 0,
});

function FoodBooking({
  showGoBack = true,
  showTimeRemaining = true,
  containerWrapperProps,
  topBoxProps,
  goBackButtonProps,
  timeRemainingTextProps,
  listingGridProps,
  listingGridItemProps,
  headerBoxProps,
  headerTextProps,
  headerSubText = "Kindly collect your food through the counter.",
  headerSubTextProps,
  snackBoxProps,
  snackHeadingProps,
  snackDescriptionProps,
  tabsProps,
  tabListProps,
  tabItemProps,
  bookingGridProps,
  snackItemProps,
  spinnerProps,
  noMoreItemsTextProps,
  bookingSummaryProps,
  timesUpModalProps,
  skeletonProps,
  voucherFieldProps,
}: FoodBookingProps) {
  const router = useRouter();
  // The voucher box is gone while the company has discount_voucher off.
  const voucherEnabled = useWebFeature("discount_voucher");

  const cinema_hall = getLocalStorageData("cinema_hall");
  const {
    order,
    loading: isOrderLoading,
    setOrder,
    clearOrder,
  } = useOrderSession();
  const orderIdx = order?.orderIdx;
  const orderShowIdx = order?.showIdx;
  const ticketCode = router.query.ticket_code as string;
  const showIdx = router.query.showIdx as string;
  const movieIdx = router?.query?.movieIdx as string;
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [cartItems, setCartItems] = useState<CartItems[]>([]);
  // Priced by the server; kept here only so the code travels with the order that is about to be
  // created. The saving itself is applied by dolby and comes back netted into
  // `order_total_amount`, which is what every later screen reads.
  const [voucher, setVoucher] = useState<VoucherValidateData | null>(null);
  const [time, setTime] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isRunning, setIsRunning] = useState(false);
  const [hasExpired, setHasExpired] = useState(false);
  const [clearSeats] = useClearSeatSelectionMutation();

  const { error: errorToast, success } = useAppToast();

  const [getBookingOrderDetail, { data: orderData }] =
    useLazyGetBookingOrderDetailQuery({});

  const { data } = useGetCategoryQuery({
    cinema_hall,
    is_parent: false,
    is_combo_category: false,
    is_active: true,
  });

  // Fetched once, unpaginated: tab switches filter these client side.
  const { data: menuItemsData, isLoading: isMenuItemsLoading } =
    useGetMenuItemsQuery({ is_active: true, cinema_hall });
  const { data: comboPackagesData, isLoading: isComboLoading } =
    useGetComboPackagesQuery({ is_active: true, cinema_hall });
  const allMenuItems = useMemo<any[]>(
    () => menuItemsData?.data ?? [],
    [menuItemsData],
  );
  const allComboPackages = useMemo<any[]>(
    () => comboPackagesData?.data ?? [],
    [comboPackagesData],
  );
  const isItemsLoading = isMenuItemsLoading || isComboLoading;
  const [proceedFoodpayment, { isLoading: isPaymentLoading }] =
    usePaymentProceedMutation();
  const [getBookingDetail, { isLoading, data: bookingDetailData, error }] =
    useLazyGetBookingDetailQuery({
      pollingInterval: ms("5min"),
    });

  const route_booking_no = router.query?.booking_no;
  const bookingDetail = bookingDetailData?.data;

  useEffect(() => {
    if (orderIdx && showIdx === orderShowIdx) {
      getBookingOrderDetail({ order_idx: orderIdx });
    }
  }, [orderIdx, showIdx, orderShowIdx, getBookingOrderDetail]);

  useEffect(() => {
    if (orderData?.data) {
      const existingCartItems: CartItems[] = [];
      if (orderData.data.menu_items && orderData.data.menu_items.length > 0) {
        orderData.data.menu_items.forEach((item: any) => {
          existingCartItems.push({
            idx: item.idx,
            name: item.title,
            price: item.amount,
            quantity: item.quantity,
          });
        });
      }

      if (
        orderData.data.combo_packages &&
        orderData.data.combo_packages.length > 0
      ) {
        orderData.data.combo_packages.forEach((combo: any) => {
          existingCartItems.push({
            idx: combo.idx,
            name: combo.title,
            price: combo.amount,
            quantity: combo.quantity,
            combo_package_items: combo.combo_package_items.map((item: any) => ({
              idx: item.idx,
              menu_item_idx: item.menu_item_idx,
              name: item.title,
            })),
          });
        });
      }

      if (existingCartItems.length > 0) setCartItems(existingCartItems);
    }
  }, [orderData]);

  useEffect(() => {
    if (showIdx) {
      const local_booking_no = getLocalStorageData("booking_no");
      getBookingDetail({
        show_idx: showIdx,
        ...(route_booking_no || local_booking_no
          ? {
              booking_no: route_booking_no || local_booking_no,
            }
          : {}),
      });
    }
  }, [showIdx, getBookingDetail, route_booking_no]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isRunning && time > 0) {
      intervalId = setInterval(() => {
        setTime((prevTime) => {
          const newTime = prevTime - 1000;
          if (newTime <= 0) {
            setIsRunning(false);
            setHasExpired(true);
            clearSeats({
              movie_show: router.query.showIdx,
            });
            clearOrder();
            onOpen();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    } else if (bookingDetail?.expire_at && !isRunning && !hasExpired) {
      const diff = bookingDetail.expire_at * 1000;
      if (diff < 300000 && diff > 0) {
        setTime(diff);
        setIsRunning(true);
      }
    } else if (!hasExpired) {
      setTime(300000);
      setIsRunning(false);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    isRunning,
    time,
    bookingDetail,
    hasExpired,
    clearOrder,
    clearSeats,
    onOpen,
    router.query.showIdx,
  ]);

  const handleAdd = useCallback((snack: any, quantity: number) => {
    setCartItems((prev) => {
      const idx = prev.findIndex((x) => x.idx === snack.idx);
      if (idx === -1) return [...prev, { ...snack, quantity: 1 }];
      if (quantity <= 0) return prev.filter((_, i) => i !== idx);
      return prev.map((item, i) => (i === idx ? { ...item, quantity } : item));
    });
  }, []);

  // What the code is priced against. The order does not exist yet, so this is the only subtotal
  // available — and it is the same arithmetic dolby will redo authoritatively on creation.
  const cartSubtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartItems],
  );

  const handleProceed = useCallback(async () => {
    const comboItems = cartItems
      ?.filter((item) => {
        return allComboPackages?.some((combo) => combo.idx === item.idx);
      })
      ?.map((nitem) => ({
        combo_package: nitem.idx,
        quantity: nitem.quantity,
        combo_package_items: nitem.combo_package_items?.map((item) => ({
          combo_package_item: item.idx,
          quantity: nitem.quantity,
          menu_item: item.menu_item_idx,
          menu_variant: null,
          is_choice_item: false,
        })),
      }));

    const menuItems = cartItems
      .filter((item) => {
        return allMenuItems.some((food) => food.idx === item.idx);
      })
      .map((r) => ({
        menu_item: r.idx,
        quantity: r.quantity,
      }));

    if (menuItems.length !== 0 || comboItems.length !== 0) {
      proceedFoodpayment({
        cinema_hall: getLocalStorageData("cinema_hall"),
        menu_items: menuItems,
        combo_packages: comboItems || [],
        ...(voucher ? { voucher_code: voucher.code } : {}),
      })
        .unwrap()
        .then(async (response) => {
          if ("data" in response && response.data) {
            const foodOrderIdx = response.data.order_idx;
            success("Food booked successfully");
            if (showIdx && movieIdx) {
              await setOrder({
                orderIdx: foodOrderIdx,
                showIdx,
                ttl: Math.ceil(time / 1000),
              });
              router.push(
                getPageRoutes(
                  "confirm_booking",
                  {
                    movieIdx,
                    showIdx,
                  },
                  {
                    cinema_hall,
                    orderIdx: foodOrderIdx,
                  },
                ),
              );
            } else {
              router.push(
                getPageRoutes(
                  "food_order",
                  { orderIdx: foodOrderIdx },
                  { ticket_code: ticketCode },
                ),
              );
            }
          }
        })
        .catch((e) => catchErrorHandler(e, errorToast));
    } else if (showIdx && movieIdx) {
      router.push(
        getPageRoutes(
          "confirm_booking",
          { movieIdx, showIdx },
          { cinema_hall },
        ),
      );
      clearOrder();
    }
  }, [
    cartItems,
    allComboPackages,
    allMenuItems,
    cinema_hall,
    clearOrder,
    errorToast,
    movieIdx,
    proceedFoodpayment,
    router,
    setOrder,
    showIdx,
    success,
    time,
    ticketCode,
    voucher,
  ]);

  const foodCatData = useMemo(
    () => data?.data.slice().sort(sortByDisplayOrder),
    [data],
  );

  // Only tabs that would show something: "All" when there are menu items, a category when
  // at least one loaded item belongs to it, and "Combo" when there are combo packages.
  const tabs = useMemo(() => {
    const usedCategories = new Set(
      allMenuItems.map((item) => item.menu_category?.idx),
    );
    return [
      ...(allMenuItems.length > 0 ? [{ key: null, label: "All" }] : []),
      ...(foodCatData ?? [])
        .filter((cat) => usedCategories.has(cat.idx))
        .map((cat) => ({ key: cat.idx, label: capitalize(cat.title) })),
      ...(allComboPackages.length > 0
        ? [{ key: COMBO_TAB, label: "Combo" }]
        : []),
    ];
  }, [allMenuItems, allComboPackages.length, foodCatData]);

  // Falls back to the first visible tab when the selected one has no data (e.g. no menu
  // items at all, only combos).
  const tabIndex = Math.max(
    tabs.findIndex((tab) => tab.key === activeTab),
    0,
  );
  const currentTab = tabs[tabIndex]?.key ?? null;

  const onTabsChange = useCallback(
    (index: number) => setActiveTab(tabs[index]?.key ?? null),
    [tabs],
  );

  const foodItemData = useMemo(
    () =>
      allMenuItems
        .filter(
          (item) =>
            currentTab === null || item.menu_category?.idx === currentTab,
        )
        .sort(sortByDisplayOrder)
        .map((item) => mapToSnackItem(item, cartItems)),
    [cartItems, allMenuItems, currentTab],
  );

  const foodComboData = useMemo(
    () =>
      allComboPackages
        .slice()
        .sort(sortByDisplayOrder)
        .map((item) => mapToSnackItem(item, cartItems)),
    [cartItems, allComboPackages],
  );

  const isComboTab = currentTab === COMBO_TAB;
  const displayData = useMemo(() => {
    if (isComboTab) {
      return foodComboData;
    } else {
      return foodItemData;
    }
  }, [isComboTab, foodComboData, foodItemData]);

  const isPageLoading = isLoading || isOrderLoading;

  if (isPageLoading) {
    return <FoodBookingSkeleton {...skeletonProps} />;
  }

  if (error) {
    return <Error statusCode={404} title="Booking has expired or not found" />;
  }
  return (
    <Box py={{ base: 6 }} {...containerWrapperProps}>
      {(showGoBack || showTimeRemaining) && (
        <Flex
          justify="space-between"
          alignItems="center"
          color="gray.500"
          fontSize="xl"
          flexWrap="wrap"
          gap={2}
          my={{ base: 4, md: "2rem" }}
          {...topBoxProps}
        >
          {showGoBack && (
            <Button
              leftIcon={<BiChevronLeft size={20} />}
              variant="ghost"
              mb={{
                base: 0,
                md: 6,
              }}
              color="gray.600"
              fontWeight="normal"
              onClick={() => router.back()}
              {...goBackButtonProps}
            >
              Go Back
            </Button>
          )}
          {showTimeRemaining && (
            <Text
              aria-live="polite"
              aria-label={`Elapsed time: ${formatMsToMinutesSeconds(time)}-food booking`}
              fontSize={{
                base: "md",
                md: "lg",
                lg: "xl",
              }}
              {...timeRemainingTextProps}
            >
              Time Remaining to book: {formatMsToMinutesSeconds(time)}
            </Text>
          )}
        </Flex>
      )}

      {/* minmax(0, …) so the scrolling tab list / long item names can't widen the columns */}
      <Grid
        templateColumns={{
          base: "minmax(0, 1fr)",
          lg: "minmax(0, 2fr) minmax(0, 1fr)",
        }}
        gap={6}
        {...listingGridProps}
      >
        <GridItem minW={0} {...listingGridItemProps}>
          <Box
            bg="blue.500"
            bgGradient="linear(to-r, blue.500, blue.400)"
            p={{ base: 5, md: 12 }}
            borderRadius="xl"
            mb={{ base: 4, md: 6 }}
            {...headerBoxProps}
          >
            <Heading
              color="white"
              size="xl"
              textAlign="center"
              {...headerTextProps}
            >
              Let&apos;s take a bite!
            </Heading>
            {headerSubText && (
              <Text
                color="whiteAlpha.900"
                fontSize={{ base: "sm", md: "md" }}
                textAlign="center"
                mt={2}
                {...headerSubTextProps}
              >
                {headerSubText}
              </Text>
            )}
          </Box>

          <Box
            bg="white"
            p={{ base: 4, md: 6 }}
            borderRadius="xl"
            backgroundColor="#F4F4F4"
            maxWidth={"1030px"}
            {...snackBoxProps}
          >
            <Heading size="md" mb={2} textAlign="center" {...snackHeadingProps}>
              Add Snacks & Drinks
            </Heading>
            <Text
              fontSize="sm"
              color="gray.600"
              mb={6}
              textAlign="center"
              {...snackDescriptionProps}
            >
              Add your favorite snacks & drinks. Get it on the counter or your
              seat hassle-free.
            </Text>

            {isItemsLoading ? (
              <Center py={12}>
                <Spinner size="lg" color="blue.500" {...spinnerProps} />
              </Center>
            ) : tabs.length === 0 ? (
              <Center py={8}>
                <Text fontSize="sm" color="gray.500" {...noMoreItemsTextProps}>
                  No food items available right now
                </Text>
              </Center>
            ) : (
              <>
                <Tabs
                  backgroundColor="white"
                  borderRadius="11px"
                  mb="10px"
                  px="10px"
                  index={tabIndex}
                  onChange={onTabsChange}
                  {...tabsProps}
                >
                  <TabList
                    w="full"
                    overflowX="auto"
                    overflowY="hidden"
                    minHeight={"fit-content"}
                    sx={{
                      scrollbarWidth: "2px",
                      ...tabListProps?.sx,
                    }}
                    {...tabListProps}
                  >
                    {tabs.map((tab) => (
                      <Tab key={tab.key ?? "all"} {...tabItemProps}>
                        {tab.label}
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>

                <Grid
                  templateColumns={{
                    base: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                    xl: "repeat(5, minmax(0, 1fr))",
                    "2xl": "repeat(6, minmax(0, 1fr))",
                  }}
                  gap={4}
                  {...bookingGridProps}
                >
                  {displayData?.map((snack) => (
                    <SnackItems
                      key={snack.idx}
                      {...snack}
                      onAdd={(q) => handleAdd(snack, q)}
                      {...snackItemProps}
                    />
                  ))}
                </Grid>
              </>
            )}

            <Text fontSize="xs" color="gray.500" mt={6}>
              *T&C Apply. Representational Image only
            </Text>
          </Box>
        </GridItem>

        <GridItem minW={0}>
          {/* Standalone food only.
           *
           * On the cart rather than the pay step, because the discount is applied when the ORDER
           * is created and netted into order_total_amount — by the checkout screen its total is
           * fixed and a code there would have nothing to act on.
           *
           * Hidden entirely when this food is being bought WITH a ticket (`showIdx`): that basket
           * is paid for in one go at the ticket checkout, which is the only place both halves are
           * known and therefore the only place a "both" code can be priced across them. Offering
           * a box here too would let a customer spend one use on the food and then be refused at
           * checkout for the tickets. */}
          {voucherEnabled && cartItems.length > 0 && !showIdx && (
            <VoucherField
              foodSubtotal={cartSubtotal}
              applied={voucher}
              onApplied={setVoucher}
              isDisabled={isPaymentLoading}
              {...voucherFieldProps}
            />
          )}
          <BookingSummary
            items={cartItems}
            bookingDetail={bookingDetail as any}
            onProceed={handleProceed}
            isLoading={isPaymentLoading}
            showIdx={showIdx}
            movieIdx={movieIdx}
            {...bookingSummaryProps}
          />
        </GridItem>
      </Grid>
      <TimesUpModal
        isOpen={isOpen}
        onClose={() => {
          setHasExpired(false);
          onClose();
        }}
        handleStartOver={() => {
          setHasExpired(false);
          router.push(
            getPageRoutes("audi_detail", {
              movieIdx,
              showIdx,
            }),
          );
        }}
        {...timesUpModalProps}
      />
    </Box>
  );
}

export default FoodBooking;
